'use client';
import { useRef } from 'react';
import Price from '@/components/commerce/price';
import Image from 'next/image';
import { MODAL_TYPES, useModal } from '@/components/modal/ModalProvider';
import { useCart } from '@/components/commerce/cart/cart-context';
import { elevarAddToCart } from '@/lib/elevar/cart-events';
import { useCampaign } from '@/app/CampaignContext';
import { gtmAddToCart } from '@/utils/events/gtmEvents';

export default function CartCrossSellItem({
  product,
  index,
  windowSize,
  recommendedProducts,
  crossSell
}) {
  const campaign = useCampaign();
  const pricingDetailsData = campaign?.pricing_details;
  const { addCartItems } = useCart();
  const { showPriceDetails, showModal } = useModal();
  const { title: productTitle, variants, featuredImage } = product || {};
  const defaultVariant = variants && variants.length > 0 ? variants[0] : null;
  const { title, compareAtPrice, price } = defaultVariant || {};
  const priceDetailsButtonRef = useRef(null);

  const iterateCrossSellProductsForModalProduct = crossSell?.products?.filter(
    (product) => product?.title === productTitle
  );

  const isMobile = windowSize?.width < 1024;
  const isSmallScreen = windowSize?.width < 640;

  const imageUrl = featuredImage?.url;
  const imageAlt = featuredImage?.alt || productTitle;

  const imageSize = isSmallScreen
    ? { width: 60, height: 60 }
    : isMobile
      ? { width: 100, height: 100 }
      : { width: 236, height: 236 };

  const handleAtcClick = async (index) => {
    try {
      const selectedProduct = recommendedProducts?.[index];
      const firstVariant = selectedProduct?.variants?.[0];

      const customAttributes = [
        { key: '_productType', value: selectedProduct?.productType },
        { key: '_productTitle', value: selectedProduct?.title },
        { key: '_productImage', value: firstVariant?.image?.url }
      ];

      const payload = [
        {
          variant: firstVariant,
          product: selectedProduct,
          quantity: 1,
          customAttributes
        }
      ];

      window.scrollTo({ top: 0, behavior: 'smooth' });

      const { error } = await addCartItems(payload, {
        suppressSuccessToast: true
      });
      // The cart context already showed the error toast.
      if (error) {
        return;
      }

      elevarAddToCart(payload);
      gtmAddToCart({ itemsToAdd: payload, page: 'Page', quantity: 1 });
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCrossSellModal();
    }
  };

  const openCrossSellModal = () => {
    showModal(MODAL_TYPES.cartCrossSell, {
      item: product,
      modalProduct: iterateCrossSellProductsForModalProduct,
      cartPage: true,
      onAddProduct: () => handleAtcClick(index, defaultVariant)
    });
  };

  const renderPriceDetails = () =>
    pricingDetailsData && (
      <button
        ref={priceDetailsButtonRef}
        type="button"
        className="body-xs text-dark-grey-text mt-1 cursor-pointer underline transition-opacity duration-300 lg:mt-0 lg:ml-1 lg:w-auto lg:pt-[2px]"
        onClick={() => {
          showPriceDetails(
            `cart-cross-sell-item-${productTitle}`,
            priceDetailsButtonRef,
            pricingDetailsData || ''
          );
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            showPriceDetails(
              `cart-cross-sell-item-${productTitle}`,
              priceDetailsButtonRef,
              pricingDetailsData || ''
            );
          }
        }}
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded="false"
        aria-controls={`price-details-cart-cross-sell-item-${productTitle}`}
        aria-label={`View price details for ${productTitle}`}
      >
        Price details
      </button>
    );

  const renderImage = () => (
    <div
      className="mr-4 cursor-pointer lg:mr-0"
      onClick={openCrossSellModal}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${productTitle}`}
    >
      <Image
        src={imageUrl}
        alt={imageAlt}
        width={imageSize.width}
        height={imageSize.height}
        sizes="(min-width: 1024px) 236px, (min-width: 640px) 100px, 60px"
        quality={100}
      />
    </div>
  );

  const formatPrice = (amount, currencyCode) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const renderButton = () => (
    <button
      id={`cart-cross-sell-button-${index}`}
      className={`${isMobile ? 'btn btn-tertiary-dark self-center' : 'btn btn-tertiary-dark mt-4 w-full self-center'}`}
      onClick={() => handleAtcClick(index, defaultVariant)}
      aria-label={`Add ${productTitle} - ${title} to cart for ${formatPrice(price?.amount, price?.currencyCode)}`}
    >
      Add
    </button>
  );

  const renderContent = () => (
    <div>
      <div
        className="body-r lg:body-l mb-1 cursor-pointer lg:mb-0"
        onClick={openCrossSellModal}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`View details for ${productTitle}`}
      >
        {productTitle}
      </div>
      <div className="body-s text-grey-text mb-1">{title}</div>
      <div className="flex flex-col items-start lg:flex-row lg:items-center">
        <div className="flex">
          <span className="price-r-crossed mr-1">
            <Price
              className="flex justify-end space-y-2 text-right"
              amount={compareAtPrice?.amount}
              currencyCode={compareAtPrice?.currencyCode}
            />
          </span>
          <Price
            className="price-r mr-1 flex justify-end space-y-2 text-right"
            amount={price?.amount}
            currencyCode={price?.currencyCode}
          />
        </div>
        {renderPriceDetails()}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <div
          key={index}
          className={`flex justify-between bg-white p-4 not-last:mb-5`}
        >
          <div className="flex items-start md:items-center">
            {renderImage()}
            {renderContent()}
          </div>
          {renderButton()}
        </div>
      ) : (
        <div
          key={index}
          className={`flex w-[276px] flex-col justify-between bg-white p-5 not-last:mr-8`}
        >
          <div className="mb-4">{renderContent()}</div>
          <div>
            {renderImage()}
            {renderButton()}
          </div>
        </div>
      )}
    </>
  );
}
